import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, ChevronDown, ChevronUp, ShoppingCart, PackageCheck, Undo2, XCircle, RotateCcw, FileText, X, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import Paginacao, { POR_PAGINA } from '../../Paginacao';

// =====================================================================
// Compras — Solicitações de Compra: CONTROLE POR PEÇA.
//
// Reformulada em 2026-08-17 (pedido da Julia). Antes a tela listava
// SOLICITAÇÕES (uma linha por diagnóstico da certificação) e as peças só
// apareciam ao expandir — para saber onde estava uma peça era preciso abrir
// documento por documento. Agora é **uma linha por peça**, com nome, código,
// fornecedor, valor e o nº do pedido do B1; o diagnóstico de origem não some,
// virou o detalhe que abre ao clicar na linha.
//
// O andamento tem 4 estados: Pendente → Pedido lançado → Comprada → Entregue
// (+ Cancelada). Quem faz o quê:
//   - GERENTE da loja (e Compras/Admin): informa o **nº do pedido do B1** assim
//     que lança o pedido lá — é o que tira a peça de "ninguém agiu";
//   - COMPRAS/Admin: marca Comprada (data, valor unitário, prazo), Entregue e
//     Cancelada.
// O recorte por loja é do backend: o gerente só recebe as peças da sua loja.
// =====================================================================

const API = '/api/compras';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// Badges de status — cores SEMÂNTICAS (não o amarelo do módulo).
// O rótulo de 'Pedido' é "Pedido lançado": numa tela que já tem a coluna
// "Pedido B1", a palavra sozinha se leria como o número, não como o estado.
// (O valor gravado no banco é 'Pedido' — ver server/compras/solicitacoes.js.)
const STATUS_CFG = {
  Pendente: { rotulo: 'Pendente', cor: '#b45309', bg: '#fef3c7' },
  Pedido: { rotulo: 'Pedido lançado', cor: '#6d28d9', bg: '#ede9fe' },
  Comprada: { rotulo: 'Comprada', cor: '#1d4ed8', bg: '#dbeafe' },
  Entregue: { rotulo: 'Entregue', cor: '#059669', bg: '#d1fae5' },
  Cancelada: { rotulo: 'Cancelada', cor: '#991b1b', bg: '#fee2e2' },
};
const ORDEM_STATUS = ['Pendente', 'Pedido', 'Comprada', 'Entregue', 'Cancelada'];

const fmtData = (d) => (d ? new Date(d).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—');
const fmtReais = (v) => `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const hojeISO = () => new Date().toISOString().slice(0, 10);
const semAcento = (s) => [...String(s || '').normalize('NFD')]
  .filter((ch) => ch.charCodeAt(0) < 0x300 || ch.charCodeAt(0) > 0x36f)
  .join('').toLowerCase();

const Badge = ({ status }) => {
  const cfg = STATUS_CFG[status] || { rotulo: status, cor: '#475569', bg: '#e2e8f0' };
  return (
    <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, color: cfg.cor, background: cfg.bg, whiteSpace: 'nowrap' }}>
      {cfg.rotulo}
    </span>
  );
};

const thStyle = { padding: '10px 8px', textAlign: 'left', fontSize: 12, fontWeight: 800, color: '#fff', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 8px', fontSize: 13, color: '#334155', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };
// Números alinham pela vírgula: tabular-nums + alinhamento à direita.
const tdNum = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const inputStyle = { padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

const ComprasSolicitacoes = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  // Gestão do andamento (comprar/entregar/cancelar) — o dinheiro.
  const podeGerir = ['Admin', 'Compras'].includes(user?.perfil);
  // Nº do pedido do B1 — inclui o GERENTE (preso à própria loja pelo backend).
  const podeInformarPedido = ['Admin', 'Compras', 'Gerente'].includes(user?.perfil);

  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [aberta, setAberta] = useState(null);       // id da peça expandida
  const [acao, setAcao] = useState(null);           // { itemId, tipo, campos... }
  const [salvandoAcao, setSalvandoAcao] = useState(false);
  // Nº do pedido: { ids: [...], numero } — a MESMA caixa serve para 1 peça e para o lote.
  const [pedido, setPedido] = useState(null);
  const [salvandoPedido, setSalvandoPedido] = useState(false);
  const [selecao, setSelecao] = useState([]);       // ids marcados no checkbox
  // Filtros (client-side — a lista chega inteira e já recortada por loja)
  const [busca, setBusca] = useState('');
  const [fLoja, setFLoja] = useState('');
  const [fFornecedor, setFFornecedor] = useState('');
  // Abre já filtrado em "Pendente" (2026-09-21): é o que precisa de ação; os selos
  // de status no topo continuam permitindo ir para "todas" ou outro status num clique.
  const [fStatus, setFStatus] = useState('Pendente');
  const [fMes, setFMes] = useState('');             // 'AAAA-MM' do diagnóstico
  const [ordem, setOrdem] = useState(null);         // { campo, dir: 'asc'|'desc' } — null = ordem do servidor
  const [pagina, setPagina] = useState(1);

  useEffect(() => { setPagina(1); }, [busca, fLoja, fFornecedor, fStatus, fMes, ordem]);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const r = await fetch(`${API}/itens`, { headers: auth() });
      if (!r.ok) throw new Error((await r.json())?.message || 'Erro ao listar as peças.');
      setItens(await r.json());
    } catch (e) { setErro(e.message); }
    setCarregando(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const nomeEquip = (i) => i.DENOMINACAO || i.EQUIPAMENTO || '';

  const lojas = useMemo(() => [...new Set(itens.map((i) => i.LOJA).filter(Boolean))].sort(), [itens]);
  const fornecedores = useMemo(() => [...new Set(itens.map((i) => i.FORNECEDOR).filter(Boolean))].sort(), [itens]);

  // Filtros SEM o de status — é sobre este conjunto que o placar conta, para os
  // números não mudarem quando o usuário clica num status (senão o placar
  // mostraria sempre "só o status escolhido" e deixaria de ser um placar).
  const filtradasSemStatus = useMemo(() => itens.filter((i) => {
    if (fLoja && i.LOJA !== fLoja) return false;
    if (fFornecedor && i.FORNECEDOR !== fFornecedor) return false;
    if (fMes && String(i.CRIADO_EM || '').slice(0, 7) !== fMes) return false;
    if (busca) {
      const q = semAcento(busca);
      const alvo = [i.DESCRICAO, i.CODIGO, i.NUMERO_PEDIDO, nomeEquip(i), i.FORNECEDOR].map(semAcento).join(' ');
      if (!alvo.includes(q)) return false;
    }
    return true;
  }), [itens, fLoja, fFornecedor, fMes, busca]);

  const filtradas = useMemo(() => (
    fStatus === 'todas' ? filtradasSemStatus : filtradasSemStatus.filter((i) => i.STATUS === fStatus)
  ), [filtradasSemStatus, fStatus]);

  const placar = useMemo(() => {
    const p = {};
    for (const st of ORDEM_STATUS) p[st] = 0;
    for (const i of filtradasSemStatus) if (p[i.STATUS] !== undefined) p[i.STATUS] += 1;
    return p;
  }, [filtradasSemStatus]);

  // Total gasto no recorte da tela: só as peças que têm valor lançado
  // (valor unitário × quantidade). Peça sem valor não entra nem como zero.
  const totalValor = useMemo(() => filtradas.reduce(
    (soma, i) => soma + (i.VALOR_UNITARIO != null ? Number(i.VALOR_UNITARIO) * Number(i.QUANTIDADE || 1) : 0), 0,
  ), [filtradas]);

  // ---- ordenação pelo cabeçalho ----
  // `ordem = null` mantém a ordem do servidor (diagnóstico mais recente primeiro),
  // que é o padrão útil de quem abre a tela para ver o que chegou.
  const valorDeOrdenacao = (item, campo) => {
    switch (campo) {
      // Status ordena pelo FLUXO (Pendente → … → Cancelada), não em ordem
      // alfabética: "Cancelada, Comprada, Entregue, Pedido, Pendente" não conta
      // história nenhuma.
      case 'STATUS': return ORDEM_STATUS.indexOf(item.STATUS);
      case 'QUANTIDADE': return Number(item.QUANTIDADE) || 0;
      // Ordena pelo valor UNITÁRIO, que é o número exibido na coluna — ordenar
      // pelo total (valor × qtd) faria a lista discordar do que está na tela.
      case 'VALOR_UNITARIO': return item.VALOR_UNITARIO != null ? Number(item.VALOR_UNITARIO) : null;
      default: return semAcento(item[campo]);
    }
  };

  const ordenadas = useMemo(() => {
    if (!ordem) return filtradas;
    const dir = ordem.dir === 'desc' ? -1 : 1;
    return [...filtradas].sort((a, b) => {
      const va = valorDeOrdenacao(a, ordem.campo);
      const vb = valorDeOrdenacao(b, ordem.campo);
      // Vazio (sem valor, sem código, sem nº de pedido) vai SEMPRE para o fim,
      // nos dois sentidos: o que falta preencher não é "o menor", é ausente.
      const aVazio = va === null || va === undefined || va === '';
      const bVazio = vb === null || vb === undefined || vb === '';
      if (aVazio && bVazio) return 0;
      if (aVazio) return 1;
      if (bVazio) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb), 'pt-BR') * dir;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- valorDeOrdenacao é pura e estável (só lê constantes do módulo)
  }, [filtradas, ordem]);

  const alternarOrdem = (campo) => setOrdem((o) => {
    if (o?.campo !== campo) return { campo, dir: 'asc' };
    if (o.dir === 'asc') return { campo, dir: 'desc' };
    return null; // 3º clique: volta à ordem natural (a do servidor)
  });

  const paginadas = ordenadas.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  // A seleção só vale para o que está visível no filtro atual — marcar uma peça
  // e depois trocar o filtro não pode gravar num item que sumiu da tela.
  const idsFiltrados = useMemo(() => new Set(filtradas.map((i) => i.ID)), [filtradas]);
  const selecionados = selecao.filter((id) => idsFiltrados.has(id));
  const alternarSelecao = (id) => setSelecao((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const todasDaPaginaMarcadas = paginadas.length > 0 && paginadas.every((i) => selecao.includes(i.ID));
  const alternarPagina = () => setSelecao((s) => (todasDaPaginaMarcadas
    ? s.filter((id) => !paginadas.some((i) => i.ID === id))
    : [...new Set([...s, ...paginadas.map((i) => i.ID)])]));

  // ---- ações ----
  const mudarStatus = async (itemId, body) => {
    setSalvandoAcao(true);
    try {
      const r = await fetch(`${API}/itens/${itemId}/status`, {
        method: 'PUT', headers: { ...auth(), 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json())?.message || 'Erro ao atualizar.');
      setAcao(null);
      await carregar();
    } catch (e) { alert(e.message); }
    setSalvandoAcao(false);
  };

  // Grava (ou apaga) o nº do pedido do B1 em 1..N peças — mesma rota nos dois casos.
  const gravarPedido = async () => {
    setSalvandoPedido(true);
    try {
      const r = await fetch(`${API}/itens/pedido`, {
        method: 'PUT', headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: pedido.ids, numero_pedido: pedido.numero }),
      });
      if (!r.ok) throw new Error((await r.json())?.message || 'Erro ao registrar o número do pedido.');
      setPedido(null); setSelecao([]);
      await carregar();
    } catch (e) { alert(e.message); }
    setSalvandoPedido(false);
  };

  const abrirPedido = (ids, numeroAtual = '') => setPedido({ ids, numero: numeroAtual || '' });

  // ---- formulário inline de ação sobre uma peça (comprar/entregar/cancelar) ----
  const FormAcao = ({ item }) => {
    if (!acao || acao.itemId !== item.ID) return null;
    const set = (patch) => setAcao((a) => ({ ...a, ...patch }));
    const conf = {
      comprar: {
        titulo: 'Marcar como comprada',
        campos: (
          <>
            <label style={{ fontSize: 12, color: '#64748b' }}>Data da compra
              <input type="date" style={{ ...inputStyle, display: 'block' }} value={acao.data_compra} onChange={(e) => set({ data_compra: e.target.value })} /></label>
            {/* Valor UNITÁRIO — a peça já tem quantidade; o total é mostrado calculado. */}
            <label style={{ fontSize: 12, color: '#64748b' }}>Valor unitário (R$)
              <input type="number" min="0" step="0.01" placeholder="0,00" style={{ ...inputStyle, display: 'block', width: 110 }}
                value={acao.valor_unitario} onChange={(e) => set({ valor_unitario: e.target.value })} />
              {acao.valor_unitario > 0 && item.QUANTIDADE > 1 && (
                <span style={{ display: 'block', marginTop: 2, color: '#94a3b8' }}>
                  total: {fmtReais(Number(acao.valor_unitario) * Number(item.QUANTIDADE))}
                </span>
              )}
            </label>
            <label style={{ fontSize: 12, color: '#64748b' }}>Prazo de entrega
              <input type="date" style={{ ...inputStyle, display: 'block' }} value={acao.prazo_entrega} onChange={(e) => set({ prazo_entrega: e.target.value })} /></label>
          </>
        ),
        body: () => ({ status: 'Comprada', data_compra: acao.data_compra || null, valor_unitario: acao.valor_unitario || null, prazo_entrega: acao.prazo_entrega || null, observacao: acao.observacao || null }),
      },
      entregar: {
        titulo: 'Confirmar entrega',
        campos: (
          <label style={{ fontSize: 12, color: '#64748b' }}>Data da entrega
            <input type="date" style={{ ...inputStyle, display: 'block' }} value={acao.data_entrega} onChange={(e) => set({ data_entrega: e.target.value })} /></label>
        ),
        body: () => ({ status: 'Entregue', data_entrega: acao.data_entrega || null, observacao: acao.observacao || null }),
      },
      cancelar: {
        titulo: 'Cancelar peça',
        campos: null,
        body: () => ({ status: 'Cancelada', observacao: acao.observacao || null }),
      },
    }[acao.tipo];

    return (
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginTop: 10, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13, color: '#0f172a' }}>{conf.titulo}</strong>
        {conf.campos}
        <label style={{ fontSize: 12, color: '#64748b', flex: 1, minWidth: 180 }}>Observação {acao.tipo === 'cancelar' ? '(motivo)' : '(opcional)'}
          <input style={{ ...inputStyle, display: 'block', width: '100%' }} value={acao.observacao} onChange={(e) => set({ observacao: e.target.value })} /></label>
        <button disabled={salvandoAcao} onClick={() => mudarStatus(item.ID, conf.body())}
          style={{ padding: '8px 16px', background: '#e3a81d', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 800, cursor: 'pointer' }}>
          {salvandoAcao ? 'Salvando…' : 'Confirmar'}
        </button>
        <button disabled={salvandoAcao} onClick={() => setAcao(null)}
          style={{ padding: '8px 12px', background: '#fff', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>
          Voltar
        </button>
      </div>
    );
  };

  // Botões da linha, conforme o status e o perfil.
  const AcoesItem = ({ item }) => {
    const btn = (rotulo, Icon, onClick, primario = false) => (
      <button key={rotulo} onClick={(e) => { e.stopPropagation(); onClick(); }} title={rotulo} style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', marginRight: 6,
        background: primario ? '#e3a81d' : '#fff', color: primario ? '#fff' : '#475569',
        border: primario ? 'none' : '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, fontWeight: 700,
        cursor: 'pointer', whiteSpace: 'nowrap',
      }}><Icon size={14} /> {rotulo}</button>
    );

    const bts = [];
    // Nº do pedido: enquanto a peça não foi comprada, é a ação natural da linha.
    if (podeInformarPedido && ['Pendente', 'Pedido'].includes(item.STATUS)) {
      bts.push(btn(item.NUMERO_PEDIDO ? 'Alterar nº' : 'Nº pedido', FileText,
        () => abrirPedido([item.ID], item.NUMERO_PEDIDO), item.STATUS === 'Pendente'));
    }
    if (podeGerir) {
      switch (item.STATUS) {
        // ⚠️ Peça PENDENTE não tem "Comprada" (decisão da Julia, 2026-08-17): a
        // ordem do processo é a loja lançar o pedido no B1 primeiro. Enquanto o
        // número não é informado, o que cabe aqui é cancelar.
        // (O backend ainda ACEITA Pendente → Comprada — é o que permite acertar
        // peça antiga ou compra direta —, mas a tela não oferece o atalho.)
        case 'Pendente':
          bts.push(btn('Cancelar', XCircle, () => setAcao({ itemId: item.ID, tipo: 'cancelar', observacao: '' })));
          break;
        case 'Pedido':
          bts.push(btn('Comprada', ShoppingCart, () => setAcao({ itemId: item.ID, tipo: 'comprar', data_compra: hojeISO(), valor_unitario: '', prazo_entrega: '', observacao: '' }), true));
          bts.push(btn('Cancelar', XCircle, () => setAcao({ itemId: item.ID, tipo: 'cancelar', observacao: '' })));
          break;
        case 'Comprada':
          bts.push(btn('Entregue', PackageCheck, () => setAcao({ itemId: item.ID, tipo: 'entregar', data_entrega: hojeISO(), observacao: '' }), true));
          // Desfazer a compra volta para onde a peça estava: 'Pedido' se o
          // pedido do B1 foi lançado, 'Pendente' se nunca houve número.
          bts.push(btn('Desfazer', Undo2, () => mudarStatus(item.ID, { status: item.NUMERO_PEDIDO ? 'Pedido' : 'Pendente' })));
          break;
        case 'Entregue':
          bts.push(btn('Desfazer entrega', Undo2, () => mudarStatus(item.ID, {
            status: 'Comprada',
            data_compra: item.DATA_COMPRA?.slice(0, 10) || null,
            prazo_entrega: item.PRAZO_ENTREGA?.slice(0, 10) || null,
          })));
          break;
        case 'Cancelada':
          bts.push(btn('Reativar', RotateCcw, () => mudarStatus(item.ID, { status: 'Pendente' })));
          break;
        default: break;
      }
    }
    return <>{bts}</>;
  };

  const colunas = 11; // p/ o colSpan da linha de detalhe

  // Cabeçalho clicável: 1º clique ordena crescente, 2º decrescente, 3º volta ao
  // natural. A seta fica sempre visível (apagada quando a coluna não é a
  // ordenada) — seta que só aparece no hover não avisa que a coluna ordena.
  const ThOrd = ({ campo, children, align = 'left' }) => {
    const ativa = ordem?.campo === campo;
    const Icone = !ativa ? ArrowUpDown : (ordem.dir === 'asc' ? ArrowUp : ArrowDown);
    return (
      <th style={{ ...thStyle, textAlign: align, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => alternarOrdem(campo)}
        title={`Ordenar por ${String(children)}`}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexDirection: align === 'right' ? 'row-reverse' : 'row' }}>
          {children}
          <Icone size={13} style={{ opacity: ativa ? 1 : 0.45 }} />
        </span>
      </th>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Solicitações de Compra</h2>
        <button onClick={carregar} title="Atualizar" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: '#fffbeb', color: '#d97706', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
          <RefreshCw size={15} /> Atualizar
        </button>
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
        Uma linha por <b>peça</b>. O caminho é <b>Pendente</b> → <b>Pedido lançado</b> (a loja lança no B1 e informa o número)
        → <b>Comprada</b> (Compras registra data, valor e prazo) → <b>Entregue</b>. Clique na linha para ver o diagnóstico de origem.
      </p>

      {/* Placar por status — clicável, faz as vezes de filtro rápido */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        {ORDEM_STATUS.map((st) => {
          const ativo = fStatus === st;
          const cfg = STATUS_CFG[st];
          return (
            <button key={st} onClick={() => setFStatus(ativo ? 'todas' : st)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999,
              background: ativo ? cfg.cor : cfg.bg, color: ativo ? '#fff' : cfg.cor,
              border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}>
              {cfg.rotulo} <b style={{ fontVariantNumeric: 'tabular-nums' }}>{placar[st]}</b>
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        {totalValor > 0 && (
          <span style={{ fontSize: 13, color: '#64748b' }}>
            Total no filtro: <b style={{ color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{fmtReais(totalValor)}</b>
          </span>
        )}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input style={{ ...inputStyle, flex: 1, minWidth: 220 }} placeholder="Buscar por peça, código, fornecedor, equipamento ou nº do pedido…"
          value={busca} onChange={(e) => setBusca(e.target.value)} />
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} style={inputStyle} title="Andamento">
          <option value="todas">Andamento (todos)</option>
          {ORDEM_STATUS.map((st) => <option key={st} value={st}>{STATUS_CFG[st].rotulo}</option>)}
        </select>
        <select value={fLoja} onChange={(e) => setFLoja(e.target.value)} style={inputStyle} title="Loja">
          <option value="">Loja (todas)</option>
          {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={fFornecedor} onChange={(e) => setFFornecedor(e.target.value)} style={inputStyle} title="Fornecedor">
          <option value="">Fornecedor (todos)</option>
          {fornecedores.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <input type="month" style={inputStyle} title="Mês do diagnóstico" value={fMes} onChange={(e) => setFMes(e.target.value)} />
      </div>

      {/* Barra do lote — só aparece com peça marcada */}
      {podeInformarPedido && selecionados.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 14px', marginBottom: 12,
        }}>
          <b style={{ fontSize: 13, color: '#92400e' }}>
            {selecionados.length} peça{selecionados.length > 1 ? 's' : ''} selecionada{selecionados.length > 1 ? 's' : ''}
          </b>
          <button onClick={() => abrirPedido(selecionados)} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#e3a81d',
            color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}><FileText size={15} /> Informar nº do pedido</button>
          <button onClick={() => setSelecao([])} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 10px', background: '#fff',
            color: '#64748b', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}><X size={14} /> Limpar seleção</button>
        </div>
      )}

      {/* Caixa do nº do pedido — a mesma para 1 peça e para o lote */}
      {pedido && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 12 }}>
          <strong style={{ fontSize: 14, color: '#0f172a' }}>
            Nº do pedido no B1 {pedido.ids.length > 1 ? `— ${pedido.ids.length} peças` : ''}
          </strong>
          <p style={{ margin: '4px 0 12px', fontSize: 12, color: '#64748b' }}>
            Informe o número do pedido de compra lançado no B1. A peça passa a <b>Pedido lançado</b>.
            Apagar o número desfaz o lançamento e volta a peça para Pendente.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input autoFocus style={{ ...inputStyle, width: 220 }} maxLength={30} placeholder="ex.: PC-4471"
              value={pedido.numero}
              onChange={(e) => setPedido((p) => ({ ...p, numero: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter' && !salvandoPedido) gravarPedido(); }} />
            <button disabled={salvandoPedido} onClick={gravarPedido} style={{
              padding: '8px 18px', background: '#e3a81d', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 800, cursor: 'pointer',
            }}>{salvandoPedido ? 'Salvando…' : 'Salvar'}</button>
            <button disabled={salvandoPedido} onClick={() => setPedido(null)} style={{
              padding: '8px 12px', background: '#fff', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: 6, fontWeight: 600, cursor: 'pointer',
            }}>Cancelar</button>
          </div>
        </div>
      )}

      {erro && <p style={{ color: '#b91c1c', fontSize: 14, fontWeight: 600 }}>{erro}</p>}

      {carregando ? <p style={{ color: '#94a3b8' }}>Carregando…</p> : filtradas.length === 0 ? (
        <p style={{ color: '#94a3b8' }}>
          Nenhuma peça com esses filtros. As peças chegam aqui sozinhas
          quando um Diagnóstico da Certificação é finalizado com peças a comprar.
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #e3a81d 0%, #d97706 100%)' }}>
                {podeInformarPedido && (
                  <th style={{ ...thStyle, width: 34 }}>
                    <input type="checkbox" checked={todasDaPaginaMarcadas} onChange={alternarPagina} title="Marcar as peças desta página" />
                  </th>
                )}
                <ThOrd campo="LOJA">Loja</ThOrd>
                <ThOrd campo="DESCRICAO">Peça</ThOrd>
                <ThOrd campo="CODIGO">Código</ThOrd>
                <ThOrd campo="FORNECEDOR">Fornecedor</ThOrd>
                <ThOrd campo="QUANTIDADE" align="right">Qtd</ThOrd>
                <ThOrd campo="VALOR_UNITARIO" align="right">Valor un.</ThOrd>
                <ThOrd campo="NUMERO_PEDIDO">Pedido B1</ThOrd>
                <ThOrd campo="STATUS">Status</ThOrd>
                <th style={thStyle}>Ações</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {paginadas.map((it) => (
                <React.Fragment key={it.ID}>
                  <tr onClick={() => { setAcao(null); setAberta(aberta === it.ID ? null : it.ID); }}
                    style={{ cursor: 'pointer', background: aberta === it.ID ? '#fffbeb' : '#fff' }}>
                    {podeInformarPedido && (
                      <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selecao.includes(it.ID)} onChange={() => alternarSelecao(it.ID)} />
                      </td>
                    )}
                    <td style={tdStyle}><b>{it.LOJA}</b></td>
                    <td style={{ ...tdStyle, minWidth: 220 }}>
                      {it.DESCRICAO}
                      {/* Chip de origem CONTORNADO — para não competir com o badge de status */}
                      {it.ORIGEM !== 'Central' && (
                        <span style={{
                          marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 999,
                          background: '#fff', border: '1px solid #cbd5e1', color: '#64748b', whiteSpace: 'nowrap',
                        }}>fora da central</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums' }}>{it.CODIGO || '—'}</td>
                    <td style={tdStyle}>{it.FORNECEDOR || '—'}</td>
                    <td style={tdNum}>{Number(it.QUANTIDADE)}</td>
                    <td style={tdNum}>{it.VALOR_UNITARIO != null ? fmtReais(it.VALOR_UNITARIO) : '—'}</td>
                    <td style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {it.NUMERO_PEDIDO || <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    <td style={tdStyle}><Badge status={it.STATUS} /></td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}><AcoesItem item={it} /></td>
                    <td style={tdStyle}>{aberta === it.ID ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</td>
                  </tr>

                  {/* Detalhe: a origem da peça (o diagnóstico) + a linha do tempo do andamento */}
                  {aberta === it.ID && (
                    <tr>
                      <td colSpan={colunas} style={{ ...tdStyle, background: '#f8fafc', padding: 16 }}>
                        <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.7 }}>
                          <div>
                            <b style={{ color: '#0f172a' }}>Origem:</b> diagnóstico do relatório de certificação
                            {' '}<b>#{it.ID_RELATORIO}</b> — {nomeEquip(it)}
                            {it.NUMERO_ATIVO ? ` (patrimônio ${it.NUMERO_ATIVO})` : ''}
                            {it.FABRICANTE ? ` · ${it.FABRICANTE}` : ''}{it.MODELO ? ` ${it.MODELO}` : ''}
                            {' · '}loja {it.LOJA} · diagnóstico de {fmtData(it.DATA_CERTIFICACAO)}
                            {it.SOLICITANTE ? ` · por ${it.SOLICITANTE}` : ''}
                            {it.MOTIVO ? ` · motivo: ${it.MOTIVO}` : ''}
                          </div>
                          <div>
                            <b style={{ color: '#0f172a' }}>Andamento:</b>
                            {it.DATA_PEDIDO && <span style={{ marginLeft: 8 }}>pedido <b>{it.NUMERO_PEDIDO}</b> informado em <b>{fmtData(it.DATA_PEDIDO)}</b></span>}
                            {it.DATA_COMPRA && <span style={{ marginLeft: 8 }}>· comprada em <b>{fmtData(it.DATA_COMPRA)}</b></span>}
                            {it.VALOR_UNITARIO != null && (
                              <span style={{ marginLeft: 8 }}>
                                · valor <b>{fmtReais(it.VALOR_UNITARIO)}</b>
                                {Number(it.QUANTIDADE) > 1 && <> /un · total <b>{fmtReais(Number(it.VALOR_UNITARIO) * Number(it.QUANTIDADE))}</b></>}
                              </span>
                            )}
                            {it.PRAZO_ENTREGA && <span style={{ marginLeft: 8 }}>· prazo <b>{fmtData(it.PRAZO_ENTREGA)}</b></span>}
                            {it.DATA_ENTREGA && <span style={{ marginLeft: 8 }}>· entregue em <b>{fmtData(it.DATA_ENTREGA)}</b></span>}
                            {!it.DATA_PEDIDO && !it.DATA_COMPRA && !it.DATA_ENTREGA && <span style={{ marginLeft: 8 }}>ainda não houve movimento nesta peça.</span>}
                          </div>
                          {it.OBSERVACAO && <div><b style={{ color: '#0f172a' }}>Obs.:</b> {it.OBSERVACAO}</div>}
                          {it.ATUALIZADO_POR && (
                            <div style={{ color: '#94a3b8' }}>
                              Última alteração por {it.ATUALIZADO_POR}{it.ATUALIZADO_EM ? ` em ${fmtData(it.ATUALIZADO_EM)}` : ''}.
                            </div>
                          )}
                        </div>
                        <FormAcao item={it} />
                      </td>
                    </tr>
                  )}

                  {/* Formulário de ação quando a linha NÃO está expandida */}
                  {acao?.itemId === it.ID && aberta !== it.ID && (
                    <tr>
                      <td colSpan={colunas} style={{ ...tdStyle, background: '#f8fafc', padding: '0 16px 12px' }}>
                        <FormAcao item={it} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          <Paginacao total={filtradas.length} pagina={pagina} onMudar={setPagina} />
        </div>
      )}
    </div>
  );
};

export default ComprasSolicitacoes;
